import React, {useEffect, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import ChangeWithZone from '../../../hoc/ChangeWithZone';
import {Images} from '../../../configs/images';
import {Image} from '../../../components/atoms/Image';
import {PageHeader} from '../../../components/organisms/PageHeader';
import {LoadingPage} from '../../../components/organisms/LoadingPage';
import {Toast} from '../../../components/atoms/Toast';
import {AddCheckPointModal} from '../../../components/organisms/AddCheckPointModal';
import {
  createHeaderAnalysisRule,
  getUserData,
  resolveHeaderAnalysisRule,
  resolveHeaderAnalysisRules,
  updateHeaderAnalysisRule,
} from '../../../api/emailTriage';
import '../HeaderAnalysis.styles.css';
import {
  addChildGroup,
  addGroupCondition,
  CHECK_DEFINITIONS,
  ConditionGroup,
  ConditionNode,
  conditionTreeFromRule,
  emptyRoot,
  HeaderCheck,
  hasDuplicateCheckInConditionGroup,
  removeCondition,
  serializeConditionGroup,
  UnsupportedConditionTreeError,
  updateCondition,
  updateGroupOperator,
} from '../headerAnalysisRuleModel';
import {MatchingEmailActionsCard} from './MatchingEmailActionsCard';
import {RuleInformationCard} from './RuleInformationCard';
import {TriggerConditionsCard} from './TriggerConditionsCard';
import {
  clearFieldErrorsForPatch,
  emptyFieldErrors,
  hasDuplicateRuleName,
  HeaderAnalysisRuleFieldErrors,
  HeaderAnalysisRuleValidationMessages,
  isDuplicateConditionGroupResponse,
  isDuplicateRuleNameResponse,
  isRuleValid,
  reconcileConditionErrors,
  updateDuplicateRuleNameError,
  validateHeaderAnalysisRule,
  validationToastMessage,
} from './headerAnalysisRuleValidation';

export const RULE_MODE = {CREATE: 'Create', EDIT: 'Edit'};

export const defaultData = {
  id: '',
  ruleName: '',
  description: '',
  threatScoreContribution: 50,
  level: 'undetermined',
  status: 'active',
  actionTaken: {priority: 'Medium', category: 'Threat', processStatus: 'Received'},
};

const LEVEL_TO_API: Record<string, string> = {
  clean: 'Clean', undetermined: 'Undetermined', threat: 'Threat',
};
const API_TO_LEVEL: Record<string, string> = {
  Clean: 'clean', Undetermined: 'undetermined', Threat: 'threat',
};

type FormData = typeof defaultData;
type ModalState = {
  open: boolean;
  mode: 'Create' | 'Edit';
  condition: ConditionNode | null;
};

type PageToast = {
  id: number;
  type: 'success' | 'error';
  message: string;
};

const cleanModal: ModalState = {
  open: false, mode: 'Create', condition: null,
};

const errorMessage = (error: any): string => {
  if (error instanceof UnsupportedConditionTreeError) return error.message;
  if (error?.response?.status === 403 || error?.response?.status === 401) {
    return 'You do not have permission to save rules in this scope.';
  }
  if (error?.response?.status === 404) return 'This rule no longer exists.';
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg).join(' ');
  if (typeof detail === 'string') return detail;
  return 'The rule could not be saved. Check your connection and try again.';
};

interface HeaderAnalysisRulePageProps {
  currentZone?: number | string | null;
  globalZone?: boolean;
}

const HeaderAnalysisRulePage: React.FC<HeaderAnalysisRulePageProps> = ({currentZone, globalZone}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();
  const {ruleId} = useParams<{ruleId: string}>();
  const basePath = location.pathname.startsWith('/settings/threat-scores')
    ? '/settings/threat-scores'
    : '/threat-scores';
  const mode = ruleId ? RULE_MODE.EDIT : RULE_MODE.CREATE;

  const [form, setForm] = useState<FormData>({...defaultData, actionTaken: {...defaultData.actionTaken}});
  const [savedRuleId, setSavedRuleId] = useState(ruleId || '');
  const [ruleType, setRuleType] = useState(globalZone ? 'Default' : 'Custom');
  const [tree, setTree] = useState<ConditionGroup>(emptyRoot());
  const [loading, setLoading] = useState(Boolean(ruleId));
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<PageToast>({id: 0, type: 'success', message: ''});
  const [dirty, setDirty] = useState(false);
  const [blockingError, setBlockingError] = useState('');
  const [loadBlocked, setLoadBlocked] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<HeaderAnalysisRuleFieldErrors>(emptyFieldErrors);
  const [resolvedRules, setResolvedRules] = useState<any[]>([]);
  const [modal, setModal] = useState<ModalState>(cleanModal);
  const originalScope = React.useRef(`${Boolean(globalZone)}:${currentZone ?? ''}`);
  const activeScope = `${Boolean(globalZone)}:${currentZone ?? ''}`;
  const scopeChanged = dirty && originalScope.current !== activeScope;
  const validationMessages: HeaderAnalysisRuleValidationMessages = {
    required: t('headerAnalysisRule.validation.required'),
    duplicateName: t('headerAnalysisRule.validation.duplicateName'),
    ruleNameTooLong: t('headerAnalysisRule.validation.ruleNameTooLong'),
    descriptionTooLong: t('headerAnalysisRule.validation.descriptionTooLong'),
    invalidScore: t('headerAnalysisRule.validation.invalidScore'),
    duplicateCondition: t('headerAnalysisRule.validation.duplicateCondition'),
    duplicateConditionGroups: t('headerAnalysisRule.validation.duplicateConditionGroups'),
    conditionDetailsRequired: t('headerAnalysisRule.validation.conditionDetailsRequired'),
    triggerDetailsRequired: t('headerAnalysisRule.messages.triggerDetailsRequired'),
    requiredFields: t('headerAnalysisRule.messages.requiredFields'),
  };
  const showErrorToast = (message: string) => setToast(current => ({
    id: current.id + 1,
    type: 'error',
    message,
  }));

  useEffect(() => {
    let active = true;
    setSavedRuleId(ruleId || '');
    setLoading(Boolean(ruleId));
    setBlockingError('');
    setLoadBlocked(false);
    setResolvedRules([]);

    const loadResolvedRules = async () => {
      try {
        const response: any = await resolveHeaderAnalysisRules({unpaginated: true});
        if (!active) return;
        const data = response?.data || response;
        const rules = Array.isArray(data?.rules) ? data.rules : [];
        if (rules.length) setResolvedRules(rules);
      } catch {
        return;
      }
    };

    const loadRule = async () => {
      if (!ruleId) return;
      try {
        const ruleResponse: any = await resolveHeaderAnalysisRule(ruleId, {});
        if (!active) return;
        const data = ruleResponse?.data || ruleResponse;
        const rule = data?.rule;
        if (!rule) {
          const notFoundError: any = new Error('Rule not found');
          notFoundError.response = {status: 404};
          throw notFoundError;
        }
        const loadedTree = conditionTreeFromRule(rule);
        setForm({
          id: rule.id,
          ruleName: rule.name,
          description: rule.description || '',
          threatScoreContribution: Number(rule.header_rule_score),
          level: API_TO_LEVEL[rule.category] || 'undetermined',
          status: rule.enabled ? 'active' : 'inactive',
          actionTaken: {
            priority: rule.actions?.priority || 'Medium',
            category: rule.actions?.category || 'Threat',
            processStatus: rule.actions?.process_status || 'Received',
          },
        });
        const apiRuleType = String(rule.type || '').toLowerCase();
        setRuleType(
          apiRuleType === 'default' || apiRuleType === 'custom'
            ? `${apiRuleType.charAt(0).toUpperCase()}${apiRuleType.slice(1)}`
            : rule.source === 'Zone'
              ? 'Custom'
              : rule.source === 'Global'
                ? 'Default'
                : globalZone
                  ? 'Default'
                  : 'Custom',
        );
        setTree(loadedTree);
      } catch (loadError: any) {
        if (!active) return;
        if (loadError instanceof UnsupportedConditionTreeError) setLoadBlocked(true);
        setBlockingError(errorMessage(loadError));
      } finally {
        if (active) setLoading(false);
      }
    };
    loadResolvedRules();
    loadRule();
    return () => { active = false; };
  }, [ruleId]);

  const mutateForm = (patch: Partial<FormData>) => {
    setForm(current => ({...current, ...patch}));
    setFieldErrors(current => clearFieldErrorsForPatch(current, patch));
    setDirty(true);
  };

  const showMetadataLengthError = (field: 'ruleName' | 'description') => {
    setFieldErrors(current => ({
      ...current,
      [field]: field === 'ruleName'
        ? validationMessages.ruleNameTooLong
        : validationMessages.descriptionTooLong,
    }));
  };

  const mutateTree = (next: ConditionGroup) => {
    setTree(next);
    setFieldErrors(current => reconcileConditionErrors(current, next));
    setDirty(true);
  };

  const setAction = (key: string, value: string) => {
    if (form.actionTaken[key as keyof typeof form.actionTaken] === value) return;
    mutateForm({
      actionTaken: {...form.actionTaken, [key]: value},
    });
  };

  const openDetail = (condition: ConditionNode) => setModal({
    open: true, mode: condition.check ? 'Edit' : 'Create', condition,
  });

  const closeModal = () => setModal(cleanModal);

  const saveCondition = (value: any) => {
    if (!modal.condition) return;
    const check = value.check as HeaderCheck;
    const duplicate = hasDuplicateCheckInConditionGroup(
      tree,
      modal.condition.id,
      check,
    );
    if (duplicate) {
      showErrorToast(validationMessages.duplicateCondition);
      closeModal();
      return;
    }
    mutateTree(updateCondition(tree, modal.condition.id, check));
    closeModal();
  };

  const remove = (conditionId: string) => mutateTree(removeCondition(tree, conditionId));

  const validateRuleNameDuplicate = () => {
    const duplicate = hasDuplicateRuleName(
      form.ruleName,
      resolvedRules,
      globalZone ? 'Global' : 'Zone',
      savedRuleId,
    );
    setFieldErrors(current => updateDuplicateRuleNameError(
      current,
      duplicate,
      validationMessages.duplicateName,
    ));
  };

  const validate = () => {
    const errors = validateHeaderAnalysisRule({
      fields: form,
      tree,
      resolvedRules,
      source: globalZone ? 'Global' : 'Zone',
      savedRuleId,
      messages: validationMessages,
    });
    setFieldErrors(errors);
    const toastMessage = validationToastMessage(errors, form, validationMessages);
    if (toastMessage) {
      setToast(current => ({
        id: current.id + 1,
        type: 'error',
        message: toastMessage,
      }));
    }
    return isRuleValid(errors);
  };

  const save = async () => {
    if (scopeChanged) {
      showErrorToast(
        'The active zone changed while this rule was being edited. '
        + 'Discard this draft and reopen the rule in the current zone.',
      );
      return;
    }
    if (!validate() || modal.open) return;
    setSaving(true);
    const payload = {
      rule: {
        name: form.ruleName.trim(),
        description: form.description.trim(),
        header_rule_score: Number(form.threatScoreContribution),
        category: LEVEL_TO_API[form.level],
        enabled: form.status === 'active',
        condition_group: serializeConditionGroup(tree),
        actions: {
          process_status: form.actionTaken.processStatus,
          priority: form.actionTaken.priority,
          category: form.actionTaken.category,
        },
      },
    };
    try {
      if (savedRuleId) {
        await updateHeaderAnalysisRule(savedRuleId, payload);
      } else {
        const response: any = await createHeaderAnalysisRule({
          ...payload,
          creatorName: getUserData().name || '',
        });
        const createdRule = response?.data?.rule || response?.rule;
        if (createdRule?.id) {
          setSavedRuleId(createdRule.id);
          setForm(current => ({...current, id: createdRule.id}));
          const createdType = String(createdRule.type || '').toLowerCase();
          if (createdType === 'default' || createdType === 'custom') {
            setRuleType(`${createdType.charAt(0).toUpperCase()}${createdType.slice(1)}`);
          }
        }
      }
      setDirty(false);
      setToast(current => ({
        id: current.id + 1,
        type: 'success',
        message: t('messages.genericSaved'),
      }));
    } catch (saveError: any) {
      if (isDuplicateRuleNameResponse(saveError)) {
        setFieldErrors(current => ({...current, ruleName: validationMessages.duplicateName}));
      } else if (isDuplicateConditionGroupResponse(saveError)) {
        showErrorToast(validationMessages.duplicateConditionGroups);
      } else {
        showErrorToast(errorMessage(saveError));
      }
    } finally {
      setSaving(false);
    }
  };

  const leave = () => {
    navigate(basePath);
  };



  return (
    <Box className="header-analysis-rule-page page-content-wrapper">
      <Box className="header-analysis-rule-header" sx={{pb: 2}}>
        <Box className="header-analysis-rule-breadcrumb">
          <IconButton className="header-analysis-back-button" onClick={leave} aria-label="Back to Threat Score Rules">
            <Image srcImg={Images.ArrowBack} width={18} height={18}/>
          </IconButton>
          <Button onClick={leave}>Threat Score Rules</Button><ChevronRightIcon/>
          <Typography>{mode === RULE_MODE.CREATE ? 'New Rule' : 'Rule Details'}</Typography>
        </Box>
        <PageHeader pageTitle={loading ? '' : savedRuleId ? (
          <Box className="header-analysis-rule-title" component="span">
            <Chip
              label={ruleType}
              className={`header-analysis-rule-type-chip header-analysis-type-chip-${ruleType.toLowerCase()}`}
            />
            <Box component="span">{form.ruleName}</Box>
          </Box>
        ) : 'Create New Rule'}/>
      </Box>
      <Box sx={{px: 2.5, pb: 6, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
        {blockingError && <Alert severity="error" sx={{mb: 2}}>{blockingError}</Alert>}

        <Box
          className="header-analysis-rule-grid"
          data-testid="rule-editor-grid"
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'minmax(0, 24fr) minmax(0, 52fr) minmax(0, 24fr)'},
            gridTemplateRows: {md: 'minmax(0, 1fr)'},
            gap: 2,
            flex: 1,
            alignItems: 'stretch',
            minHeight: 0,
            '& .MuiTypography-root:not(.rule-card-heading):not(.threat-scoring-heading):not(.score-range-text)': {
              fontSize: '14px',
            },
            '& .MuiInputBase-input, & .MuiFormControlLabel-label, & .MuiButton-root, & .MuiChip-label': {
              fontSize: '14px',
            },
          }}
        >
          {!loading && (
            <>
              <RuleInformationCard
                ruleName={form.ruleName}
                status={form.status}
                description={form.description}
                fieldErrors={fieldErrors}
                onChange={mutateForm}
                onLengthLimitExceeded={showMetadataLengthError}
                onRuleNameBlur={validateRuleNameDuplicate}
              />

              <TriggerConditionsCard
                tree={tree}
                emptyConditionsError={fieldErrors.emptyConditions}
                duplicateConditionGroupsError={fieldErrors.duplicateConditionGroups}
                conditionErrors={fieldErrors.conditionDetails}
                onOperator={(groupId, operator) => mutateTree(updateGroupOperator(tree, groupId, operator))}
                onAddGroupCondition={groupId => mutateTree(addGroupCondition(tree, groupId))}
                onAddGroup={() => mutateTree(addChildGroup(tree))}
                onDetail={openDetail}
                onRemove={remove}
              />

              <MatchingEmailActionsCard
                level={form.level}
                threatScoreContribution={form.threatScoreContribution}
                actionTaken={form.actionTaken}
                scoreError={fieldErrors.score}
                onChange={mutateForm}
                onActionChange={setAction}
              />
            </>
          )}
        </Box>
      </Box>

      <Drawer
        variant="persistent"
        anchor="bottom"
        open={dirty}
        className="save-change-container"
        data-testid="rule-save-drawer"
      >
        <Box className="save-change-content" sx={{py: 2, textAlign: 'center'}}>
          <Button variant="outlined" onClick={leave} sx={{mr: 1}}>Cancel</Button>
          <Button variant="contained" disabled={saving || modal.open || scopeChanged || loadBlocked} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </Box>
      </Drawer>

      <AddCheckPointModal
        open={modal.open}
        mode={modal.mode}
        onClose={closeModal}
        onSave={saveCondition}
        selectedData={modal.condition?.check ? {
          id: modal.condition.id,
          check: modal.condition.check,
          ...CHECK_DEFINITIONS[modal.condition.check],
        } : {}}
      />
      <Toast
        isOpen={Boolean(toast.message)}
        type={toast.type}
        message={toast.message}
        toastId={toast.id}
        autoHideDuration={2000}
      />
      <LoadingPage open={loading}/>

    </Box>
  );
};

export const HeaderAnalysisRuleEditor = ChangeWithZone(HeaderAnalysisRulePage, {
  preserveRuleEditorOnZoneChange: true,
});
